import { Ref } from "react";
import { useMediaById } from "@coworker/app/src/components/RoomSettings/hooks/useMediaById";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import TitledTextWithModal from "@coworker/apprestructured/src/task-management/components/TitledTextWithModal/TitledTextWithModal";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import { MediaGroupedListView } from "@coworker/apprestructured/src/shared/components/MediaGroupedListView/MediaGroupedListView";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { useGroupsByMediaId } from "@coworker/app/src/components/RoomSettings/hooks/useGroupsByMediaId";
import { useTranslation } from "@coworker/locales";
import { groupMediasByType } from "@coworker/apprestructured/src/task-management/helpers/media.helper";

export type MediaLocationCreatorFragmentConfigProps = {
  field: string;
  title: string;
};

type MediaLocationCreatorFragmentProps = {
  configProps: MediaLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function MediaLocationCreatorFragment({
  configProps,
  commonProps,
}: MediaLocationCreatorFragmentProps) {
  const { field, title } = configProps;
  const { task, taskType } = commonProps;
  const { t } = useTranslation();
  const mediaId = validateInitialDetailsFieldValue<string>(
    getByPath(task, field),
    field,
    taskType
  );
  const rawGroupId = getByPath(task, "details.group_id");
  const groupId = typeof rawGroupId === "string" ? rawGroupId : undefined;
  const hasMediaId = typeof mediaId === "string" && mediaId.length > 0;

  const { data: mediaResponse, isLoading: isMediaLoading } = useMediaById(
    isLoading(mediaId) ? null : mediaId
  );
  const { data: groups } = useGroupsByMediaId(hasMediaId ? mediaId : undefined);

  const media = mediaResponse?.media;

  if (isLoading(mediaId) || isMediaLoading) {
    return <LoadingCreatorFragment />;
  }

  if (!media) {
    return null;
  }

  const mediaType = (media.mediaType ?? MediaTypes.NONE) as MediaTypes;
  const groupName = groups?.find((group) => group.id === groupId)?.name ?? "";
  const medias: MediasAndArticleCountResponse[] = [
    {
      ...media,
      mediaId: media.id,
      mediaType,
      area: media.areaHfb,
      nbrArticles: 0,
      parentArticleCombinationNumber: undefined,
      articleId: "",
      groupId: groupId ?? "",
      groupName,
      parentMediaName: mediaResponse?.parent?.name ?? "",
    },
  ];
  const groupByMediaType = groupMediasByType(medias);

  return (
    <TitledTextWithModal
      title={t(title)}
      description={media.name}
      modalContent={
        <MediaGroupedListView
          groupedMediasByType={groupByMediaType}
          showQuantityLabel={false}
        />
      }
    />
  );
}

export default MediaLocationCreatorFragment;
